import type { ResourceVersionStatusView } from '@/types/versioning';

/**
 * The read-only version status indicator (task 725).
 *
 * Every versioned resource's detail and edit view carries this in its header,
 * visually separate from any action button, answering one question at a glance:
 * *what state is this resource in?* It is deliberately NOT an action — clicking
 * it opens the version history panel, and nothing else in the app depends on
 * having clicked it.
 *
 * Four states, three of which render:
 *
 *   1. **Dormant** (`live-editing`) — the Account has not adopted Publishing &
 *      versions, and nothing renders at all (task 979). The chip used to say
 *      "Live editing" here; the platform does not use that word anywhere, and
 *      the absence of a status is itself the signal that the practice has not
 *      been adopted. The state name stays — it is the status route's own
 *      vocabulary — only what renders for it changed.
 *   2. **Not yet published** — adopted, nothing frozen yet. The hover title
 *      still says explicitly that edits take effect immediately, since adopting
 *      the practice is exactly when a User assumes edit behaviour changed; the
 *      label itself dropped its "Tracked ·" prefix (task 782), which reaches
 *      every versioned resource page and is meant to.
 *   3. **Published · clean** — "v1.0 published".
 *   4. **Draft in progress** — "Editing v1.1 (draft) · v1.0 published".
 *
 * The chip stays a one-liner. Which environments serve which version, and which
 * endpoint versions pin this resource, live in the history panel — never here.
 */

interface VersionStatusChipProps {
  status: ResourceVersionStatusView;
  /** Opens the version history panel. Omitted in the dormant state. */
  onOpenHistory?: () => void;
}

interface ChipCopy {
  label: string;
  title: string;
  classes: string;
}

/** `null` for the dormant state, which renders nothing at all (task 979). */
const chipCopy = (status: ResourceVersionStatusView): ChipCopy | null => {
  switch (status.state) {
    case 'tracked-unpublished':
      return {
        label: 'Not yet published',
        title: 'Nothing is frozen yet — your edits still take effect immediately.',
        classes:
          'border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300',
      };
    case 'published-clean':
      return {
        label: `v${status.publishedVersion} published`,
        title: 'This resource matches its newest published version.',
        classes:
          'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200',
      };
    case 'draft-in-progress':
      return {
        label: `Editing v${status.workingVersion} (draft) · v${status.publishedVersion} published`,
        title:
          'Your edits are landing on a new draft version. The published version keeps serving unchanged.',
        classes:
          'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-200',
      };
    case 'live-editing':
    default:
      return null;
  }
};

export function VersionStatusChip({ status, onOpenHistory }: VersionStatusChipProps) {
  const copy = chipCopy(status);

  // Dormant: an Account that has not adopted Publishing & versions sees no
  // version status at all (task 979).
  if (!copy) return null;

  const classes = `inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${copy.classes}`;

  // No history to open, so the indicator is plain text rather than a control
  // that leads nowhere.
  if (!onOpenHistory) {
    return (
      <span
        data-testid="version-status-chip"
        data-state={status.state}
        title={copy.title}
        className={classes}
      >
        {copy.label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpenHistory}
      data-testid="version-status-chip"
      data-state={status.state}
      title={`${copy.title} Open version history.`}
      aria-label={`${copy.label}. Open version history.`}
      className={`${classes} transition-colors hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:brightness-110`}
    >
      {copy.label}
    </button>
  );
}

export default VersionStatusChip;
